import { Feature } from "ol";
import { Coordinate } from "ol/coordinate";
import { Geometry, GeometryCollection, Polygon } from "ol/geom";
import generateSymbol from "../common/symbols";
import ParserBase, { TokenType } from "./parserBase";

const URN_TOKENS = [
    "S=112", // klasicka urna
    "S=113", // neidentifikovany hrob
    "S=114"  // projektovana urna
];

export default class GraveParser extends ParserBase {
    graveTypes: string[];

    constructor(src: string) {
        super(src);
        this.graveTypes = [];
    }

    parseGeometry(): Geometry {
        return undefined;
    }

    parseFeature():Feature {
        let token = this.skipFeaturesUntilGrave();
        if (token === TokenType.EOF)
            return undefined;
        
        let objectNameParts = this._lines[this._cursor].replace("&O", "").trim().split(" ");
        let attributes: {[key:string]:any} = {
            layer: objectNameParts[0],
            objectNumber: objectNameParts[1] || undefined
        };
        let continueParsing:boolean = true;
        let geometries:Geometry[] = [];
        while (continueParsing) {
            token = this.readLine();
            switch (token) {
                case TokenType.LINE:
                    let geometryResult = this.parseSingleGeometry();
                    if (geometryResult.result) {
                        geometries.push(geometryResult.result);
                        if (attributes["layer"] === "HROBY" && geometryResult.isProjected) {
                            attributes["isProjected"] = true;
                        }
                    }
                    continueParsing = geometryResult.continueParsing;
                    if (continueParsing)
                        this._cursor--;
                break;
                case TokenType.ATTRIBUTE:
                    let [k, v] = this.parseAttribute();
                    attributes[k] = v;
                    if (k == "hrob") {
                        attributes = {...attributes, ...this.parseGraveNumbers(v)};
                    }
                    if (k == "graveType") {
                        if (!this.graveTypes.includes(v)) {
                            this.graveTypes.push(v);
                        }
                    }
                break;
                case TokenType.FEATURE:
                case TokenType.EOF:
                    continueParsing = false;
                break;
            }
        }
        if (geometries.length === 0) {
            return undefined;
        } else {
            return new Feature({ 
                geometry: (geometries.length === 1) ? geometries[0] : new GeometryCollection(geometries),
                ...attributes
            });
        }
    }

    private skipFeaturesUntilGrave():TokenType {
        let token = this.skipUntil(TokenType.FEATURE);
        while (token !== TokenType.EOF) {
            if (this._lines[this._cursor].split(" ")[1].trim() === "HROBY")
                break;
            this.readLine();
            token = this.skipUntil(TokenType.FEATURE);
        }
        return token;
    }
    
    private parseSingleGeometry():{result: Geometry, isProjected: boolean, continueParsing: boolean} {
        let token = this.skipUntil(TokenType.LINE);
        if (token === TokenType.EOF)
            return undefined;

        let segments : Coordinate[][] = [];
        let currentCoordinates : Coordinate[] = [];
        let shouldParseNextLine:boolean = true;
        let isProjected:boolean = false;

        while (shouldParseNextLine) {
            let currentLine = this._lines[this._cursor].replace("&L", "").trim();
            let tokens = currentLine.split(" ");
            isProjected = isProjected || tokens.some(t => t == "K=6");
            if (currentCoordinates.length > 0 && tokens[0] === "P") {
                segments.push(currentCoordinates);
                currentCoordinates = [];
            }
            let symbolToken = tokens.find(t => t.startsWith("S="));
            if (tokens[0] === "P" && typeof(symbolToken) !== "undefined" && URN_TOKENS.findIndex(ut => ut === symbolToken) > -1) {
                let point = [-tokens[1]+this._offset.y, -tokens[2]+this._offset.x];
                let scaleToken = tokens.find(t => t.startsWith("M="));
                let angleToken = tokens.find(t => t.startsWith("U="));
                let symbolCoords = generateSymbol(point, symbolToken, angleToken, scaleToken);
                return {result: new Polygon(symbolCoords, "XY"), isProjected: isProjected, continueParsing: false};
            } else if (tokens[0] === "L" || tokens[0] === "P") {
                let coords = [-tokens[1]+this._offset.y, -tokens[2]+this._offset.x];
                currentCoordinates.push(coords);
            }
            this._cursor++;
            if (this._cursor > this._lines.length || this._lines[this._cursor].trim().startsWith("&")) {
                shouldParseNextLine = false;
            }
        }
        if (currentCoordinates.length > 1)
            segments.push(currentCoordinates);

        segments = segments.filter((s) => s && s.length > 2);

        if (segments.length === 0)
            return {result: undefined, isProjected: isProjected, continueParsing: true};
        else
            return {result: new Polygon(segments, "XY"), isProjected: isProjected, continueParsing: true}; 
    }

    private parseGraveNumbers(pattern: string) :{[key:string]:any} {
        let result:{[key:string]:any} = {};
        let placeTokens = pattern.split(",");
        let podlomenieChodnika = "";
        let firstPlaceTokens = placeTokens[0].split(".");
        result["sektor"] = firstPlaceTokens[0].trim();
        let identifier = firstPlaceTokens[0].trim();
        if (firstPlaceTokens.length > 3)
            podlomenieChodnika =  (firstPlaceTokens[3] && firstPlaceTokens[3].trim()) ? firstPlaceTokens[3].trim() : "";
        result["chodnik"] = firstPlaceTokens[1].trim() + (podlomenieChodnika !== "" ? "/"+podlomenieChodnika : "");
        identifier += "__" + firstPlaceTokens[1].trim() + (podlomenieChodnika !== "" ? "_"+podlomenieChodnika : "") + "__";
        result["hroby"] = [];
        result["graveIds"] = [];
        if (firstPlaceTokens.length > 4 && firstPlaceTokens[4] && firstPlaceTokens[4].trim() !== "") {
            result["hroby"].push(firstPlaceTokens[2].trim() + "/" + firstPlaceTokens[4].trim());
            result["graveIds"].push(identifier + firstPlaceTokens[2].trim() + "_" + firstPlaceTokens[4].trim());
        } else {
            result["hroby"].push(firstPlaceTokens[2].trim());
            result["graveIds"].push(identifier + firstPlaceTokens[2].trim());
        }
        for (let i = 1; i < placeTokens.length; i++) {
            let additionalTokens = placeTokens[i].split(".");
            if (additionalTokens.length > 2 && additionalTokens[2] && additionalTokens[2] !== "") {
                result["hroby"].push(additionalTokens[0] + "/" + additionalTokens[2]);
                result["graveIds"].push(identifier + additionalTokens[0].trim()+ "_" + additionalTokens[2].trim());
            } else {
                result["hroby"].push(additionalTokens[0]);
                result["graveIds"].push(identifier + additionalTokens[0].trim());
            }
        }
        return result;
    }
}